import { CSSProperties, ReactNode } from 'react'
import cx from 'classnames'
import { GlassSurface } from '~m/designSystem'
import styles from './ReconcileRow.module.css'

/**
 * Visual state of a reconciliation row.
 *
 * - `default` — the row still matches the suggested resolution.
 * - `modified` — the user changed the suggestion.
 * - `conflict` — the resolution clashes with another row.
 * - `skipped` — the record is left out of the sync. The source
 *   fades and its name is struck through.
 */
export type ReconcileRowState =
  | 'default'
  | 'modified'
  | 'conflict'
  | 'skipped'

export interface ReconcileRowProps {
  /** Left column — usually a SourceCard */
  source: ReactNode
  /** Centre column — usually a ConfidenceBridge */
  bridge: ReactNode
  /** Right column — the resolution controls */
  resolution: ReactNode
  /** Full-width footer, e.g. a conflict explanation */
  alert?: ReactNode
  /** Row state, driving the accent stripe and border */
  state?: ReconcileRowState
  /**
   * Position in the list. Used to stagger the entry animation
   * so rows cascade in.
   */
  index?: number
  /** Additional CSS class */
  className?: string
}

/**
 * The layout for a single reconciliation decision: an incoming
 * source record on the left, a confidence bridge in the middle,
 * and the resolution controls on the right. Uses a GlassSurface
 * card shell and collapses to a single column on narrow screens.
 */
export function ReconcileRow({
  source,
  bridge,
  resolution,
  alert,
  state = 'default',
  index = 0,
  className,
}: ReconcileRowProps) {
  const style = {
    animationDelay: `${index * 45}ms`,
  } as CSSProperties

  return (
    <GlassSurface
      variant="card"
      style={style}
      className={cx(styles.ReconcileRow, className, {
        [styles.isModified]: state === 'modified',
        [styles.isConflict]: state === 'conflict',
        [styles.isSkipped]: state === 'skipped',
      })}
    >
      {state !== 'default' && (
        <span
          className={styles.ReconcileRow__stripe}
          aria-hidden="true"
        />
      )}
      <div className={styles.ReconcileRow__source}>{source}</div>
      <div className={styles.ReconcileRow__bridge}>{bridge}</div>
      <div className={styles.ReconcileRow__resolution}>
        {resolution}
      </div>
      {alert && (
        <div className={styles.ReconcileRow__alert}>{alert}</div>
      )}
    </GlassSurface>
  )
}
